import React, { useMemo } from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import type { IconName } from '../../element/Icon/icon.types';

import { AccordionIconPosition, AccordionRotation } from './accordions.types';
import type { AccordionShadow } from './accordions.types';
import { AccordionsGroupContext } from './AccordionsContext';

export type AccordionsProps = {
  /**
   * Eases a section open and shut rather than swapping it in and out, and says how far the marker
   * turns on the way. **It also keeps every section mounted**, which it has to: the default unmounts
   * a shut one, and there is nothing to animate about content that is not there.
   *
   * The reveal is a grid track going from `0fr` to `1fr`, so it needs no measurement and no height
   * stated anywhere - a section is eased to exactly as tall as it turns out to be.
   *
   * Left off, the marker still turns a quarter - it is the only thing saying whether a section is
   * open - but it snaps rather than eases, and a shut section is unmounted as before.
   */
  animated?: AccordionRotation;
  children: React.ReactNode;
  className?: string;
  /**
   * The mark beside a heading, turned rather than swapped when the section opens - so it has to be
   * a glyph that still reads at a quarter or half turn. On the group, because a run whose sections
   * were marked differently would not read as one control.
   */
  icon?: IconName;
  /**
   * Whether the marker sits at the far edge of the heading's row or immediately after the words.
   *
   * On the group for the same reason as `icon`: a run whose markers did not line up with each other
   * would read as several controls rather than one.
   */
  iconPosition?: AccordionIconPosition;
  minimal?: boolean;
  shadow?: AccordionShadow;
  name?: string;
};

export const Accordions = ({
  animated,
  children,
  className,
  icon = 'arrow-right',
  iconPosition = AccordionIconPosition.END,
  minimal,
  shadow,
  name,
}: AccordionsProps) => {
  const groupValue = useMemo(
    () => ({ animated, icon, iconPosition }),
    [animated, icon, iconPosition]
  );

  return (
    <div
      data-test={name}
      className={AttrsHelper.formatClassname(
        'accordions',
        animated && 'animated-accordion',
        /*
          Always stated, and defaulting to the quarter turn the prop is off: the turn is what says a
          section is open, so it cannot be a thing only an animated accordion gets.
        */
        `accordion-rotate-${animated ?? AccordionRotation.QUARTER}`,
        minimal && 'minimal-accordion',
        shadow ? `shadow-${shadow}` : 'flat-accordion',
        className
      )}
    >
      <AccordionsGroupContext.Provider value={groupValue}>
        {children}
      </AccordionsGroupContext.Provider>
    </div>
  );
};
